import { useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import { PDFViewer } from "@/components/viewers/PDFViewer";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { ContentComingSoon } from "@/components/viewers/ContentComingSoon";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";

export function NotesPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Concept Notes");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "notes",
    active: paywall === null,
    minDwellSeconds: 120,
  });

  // Completion = last page reached AND the dwell floor met. Flicking the
  // scrollbar to the bottom is not finishing a chapter.
  const handlePageChange = (page: number, totalPages: number) => {
    if (totalPages > 0 && page >= totalPages) reportEndReached();
  };

  const { url, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "notes",
    enabled: shouldFetch,
  });

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Concept Notes"
      modeIcon={FileText}
      modeAccent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      showHeader={false}
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Concept notes" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : isNotFound ? (
          <ContentComingSoon
            chapterId={chapterId}
            chapterName={chapter?.chapter_name}
            title="The concept notes for this chapter are almost ready"
            modeIcon={FileText}
            modeAccent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          />
        ) : url ? (
          <PDFViewer
            url={url}
            title="Concept Notes"
            subtitle={chapter?.chapter_name ?? null}
            modeIcon={FileText}
            modeAccent="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            showStudyControls
            onPageChange={handlePageChange}
            className="h-[100dvh] min-h-0"
          />
        ) : isLoading ? (
          <div className="flex-1 grid place-items-center text-sm text-text-muted">
            Preparing your notes…
          </div>
        ) : null
      )}
    </ViewerScaffold>
  );
}
